import { Injectable } from '@angular/core';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { SERVICE_URL } from '../../_urls/service.urls';

@Injectable({
    providedIn: 'root'
})
export class KeyService {
    private db!: IDBDatabase;
    derivedKey: any;

    constructor(private keycloakService: KeycloakService) {
        this.initDB();
    }

    /**
   * Init Index DB
   * @returns
   */
    initDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open('crypto-keys', 2);
            request.onupgradeneeded = (event: any) => {
                this.db = event.target.result;
                this.db.createObjectStore('keys', { keyPath: 'name' });
            };
            request.onsuccess = async (event: any) => {
                this.db = event.target.result;
                this.generateKeys();
                resolve(this.db);
            };
            request.onerror = (event: any) => {
                reject(event.target.error);
            };
        });
    }

    /**import { HttpService } from '../_http/http.service';

   * Insert key in indexed db
   */
    async generateKeys() {
        const keyPair = await window.crypto.subtle.generateKey(
            {
                name: 'ECDH',
                namedCurve: 'P-256'
            },
            false,
            ['deriveKey', 'deriveBits']
        );

        const signKeyPair = await window.crypto.subtle.generateKey(
            {
                name: 'ECDSA',
                namedCurve: 'P-256'
            },
            false,
            ['verify', 'sign']
        );

        const transaction = this.db.transaction(['keys'], 'readwrite');
        const store = transaction.objectStore('keys');
        store.put({ name: 'client-key', key: keyPair });
        store.put({ name: 'sign-key', key: signKeyPair });
    }

    /**
   *  Get Client Keys
   * @returns client key (UI)
   */
    async getClientKey() {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['keys'], 'readonly');
            const store = transaction.objectStore('keys');
            const request = store.get('client-key');
            request.onsuccess = () => resolve(request.result.key);
            request.onerror = () => reject(request.error);
        });
    }

    /**
   * Get Sign Key
   * @returns Sign Key
   */
    async getSignKey() {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['keys'], 'readonly');
            const store = transaction.objectStore('keys');
            const request = store.get('sign-key');
            request.onsuccess = () => resolve(request.result.key);
            request.onerror = () => reject(request.error);
        });
    }

    getKid(url: any) {
        if(url.includes(SERVICE_URL.KEYCLOCK_SERVICE)) {
            return this.keyClockJwk.keys[0].kid;
        }else {
            return this.fdmJwk.keys[0].kid;
        }
    }

    /**
   * Get Server Public Key
   * @param jwksUrl
   * @returns key from server
   */
    keyClockJwk:any;
    fdmJwk: any;
    serverKeyRes: any;
    jwk:any;
    keyClockKeyRes: any;

    async getServerPublicKey(jwksUrl: string, url: any): Promise<CryptoKey> {

        if(url.includes(SERVICE_URL.KEYCLOCK_SERVICE)) {
            if (!this.keyClockKeyRes) {
                const response = await fetch(jwksUrl);
                const jwks = await response.json();
                this.keyClockJwk = jwks;
                const serverKey = jwks.keys[0];
                this.keyClockKeyRes = serverKey;
            }
        } else {
            if (!this.serverKeyRes) {
                const response = await fetch(jwksUrl);
                const jwks = await response.json();
                this.fdmJwk = jwks;
                const serverKey = jwks.keys[0];
                this.serverKeyRes = serverKey;
            }
        }

        return crypto.subtle.importKey(
            'jwk',
            url.includes(SERVICE_URL.KEYCLOCK_SERVICE) ? this.keyClockKeyRes :  this.serverKeyRes,
            {
                name: 'ECDH',
                namedCurve: 'P-256'
            },
            true,
            []
        );
    }

    // /**
    //  * Export Key to Base64
    //  * @param cryptoKeyPair
    //  * @returns
    //  */
    // exportKeyToBase64(cryptoKeyPair: CryptoKeyPair): Promise<{ publicKeyBase64: string; privateKeyBase64: string }> {
    //   return new Promise((resolve, reject) => {
    //     crypto.subtle.exportKey('pkcs8', cryptoKeyPair.privateKey)
    //       .then((privateKeyRaw) => {
    //         const privateKeyUint8 = new Uint8Array(privateKeyRaw);
    //         const privateKeyBase64 = btoa(
    //           String.fromCharCode(...privateKeyUint8)
    //         );

    //         crypto.subtle.exportKey('spki', cryptoKeyPair.publicKey)
    //           .then((publicKeyRaw) => {
    //             const publicKeyUint8 = new Uint8Array(publicKeyRaw);
    //             const publicKeyBase64 = btoa(String.fromCharCode(...publicKeyUint8));
    //             resolve({ publicKeyBase64, privateKeyBase64 });
    //           })
    //           .catch(reject);
    //       })
    //       .catch(reject);
    //   });
    // }

    async signJwt(header: any, method: string, url: string) {
        const keyPair: any = await this.getSignKey();
        const clientKeyPair: any = await this.getClientKey();
        const privateKey = keyPair.privateKey;

        const JWKDPOPPublicKey = await window.crypto.subtle.exportKey(
            'jwk',
            keyPair.publicKey
        );
        const JWKClientPublicKey = await window.crypto.subtle.exportKey(
            'jwk',
            clientKeyPair.publicKey
        );

        const dpopHeader = {
            typ: 'dpop+jwt',
            alg: 'ECDSA',
            jwk: JWKDPOPPublicKey
        };

        const payload = {
            jti: crypto.randomUUID(),
            htm: method,
            htu: url,
            iat: Math.floor(Date.now() / 1000),
            ath: await this.hashToken(await this.keycloakService.getToken()),
            enc: {
                jwk: JWKClientPublicKey,
                kid: this.serverKeyRes.kid,
                iv: this.arrayBufferToBase64(header.iv)
            }
        };

        const encoder = new TextEncoder();
        const encodedHeader = this.arrayBufferToBase64(
            encoder.encode(JSON.stringify(dpopHeader))
        );
        const encodedPayload = this.arrayBufferToBase64(
            encoder.encode(JSON.stringify(payload))
        );

        const data = encoder.encode(`${encodedHeader}.${encodedPayload}`);
        const signature = await crypto.subtle.sign(
            {
                name: 'ECDSA',
                hash: { name: 'SHA-256' }
            },
            privateKey,
            data
        );

        const encodedSignature = this.arrayBufferToBase64(
            new Uint8Array(signature)
        );
        return `${encodedHeader}.${encodedPayload}.${encodedSignature}`;
    }

    /**
   * Hash Token using SHA-256
   * @param jwtToken
   * @returns
   */
    async hashToken(jwtToken: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(jwtToken);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
        return hashHex;
    }

    /**
   * Convert ArrayBuffer to Base64
   * @param buffer
   * @returns Base64
   */
    arrayBufferToBase64(buffer: any) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    /**
   * Convert Base64 to ArrayBuffer
   * @param base64
   * @returns ArrayBuffer
   */
    base64ToArrayBuffer(base64: any) {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
   * Convert object to Base64 string (browser-friendly, Unicode-safe, no deprecated APIs)
   * @param obj The object to encode
   * @returns Base64-encoded string
   */
    objectToBase64(obj: any): string {
        const json = JSON.stringify(obj);
        // Encode JSON string to UTF-8 bytes
        const utf8Bytes = new TextEncoder().encode(json);
        // Convert bytes to binary string
        let binary = '';
        utf8Bytes.forEach(byte => binary += String.fromCharCode(byte));
        // Encode binary string to Base64
        return btoa(binary);
    }

    /**
   * Convert Base64 string back to object (browser-friendly, Unicode-safe, no deprecated APIs)
   * @param base64 The Base64 string to decode
   * @returns The original object
   */
    base64ToObject(base64: string): any {
    // Decode Base64 to binary string
        const binary = atob(base64);
        // Convert binary string to UTF-8 bytes
        const bytes = new Uint8Array([...binary].map(char => char.charCodeAt(0)));
        // Decode UTF-8 bytes to JSON string
        const json = new TextDecoder().decode(bytes);
        return JSON.parse(json);
    }

    /**
   * Decode Response
   * @param response
   * @param xCryptoEnabled
   * @returns
   */
    async decodeResponse(response: any, xCryptoEnabled: any) {
        const encryptedData = this.base64ToArrayBuffer(response);
        const ivFromHeader = this.base64ToArrayBuffer(xCryptoEnabled);
        try {
            const decryptedBody = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivFromHeader },this.derivedKey, encryptedData);
            const decoder = new TextDecoder();
            const decodedBody = decoder.decode(decryptedBody);
            return JSON.parse(decodedBody);
        }
        catch (error) {
            console.error('Error while decrypting response', error);
            return {};
        }
    }
}
